import { Log } from '../AuxillaryService/Log';
import type { Zone } from '../Zone';
import type { Display } from './Display';

/** List of managed Displays */
const displays: Array<Display> = [];
/** ID of the current active Display */
let currentDisplayId = -1;

/**
 * Add Display(s) to the list of managed Displays
 */
export function pushDisplay(...newDisplays: Array<Display>): number {
    return displays.push(...newDisplays);
}

/**
 * Get list of managed Displays
 */
export function getDisplays(): typeof displays {
    return displays;
}

/**
 * Set the current active Display
 * @throws {ReferenceError} if no Display exists with the given ID
 */
export function setCurrentDisplayId(displayId: number): void {
    if (displays.find((d) => d.id === displayId) === undefined) {
        throw new ReferenceError(`A Display with ID #${displayId} does not exist`);
    }
    currentDisplayId = displayId;
}

/**
 * gets the display variable for the currently running display
 * @throws {Error} when there is no current display. This should only happen if executed before
 * Displays start running.
 * @throws {ReferenceError} if the current Display does not exist. This should never happen.
 */
function getCurrentDisplay(): Display {
    if (currentDisplayId === -1) {
        throw new Error('There is no current Display.');
    }

    const display = getDisplayById(currentDisplayId);

    return display;
}

/**
 * Pauses all the zones in the currently running display
 */
export async function pauseCurrentDisplay(): Promise<void> {
    return await getCurrentDisplay().pauseAllZones();
}

/**
 * Plays (resumes) all the zones in the currently running display (or at least
 * the display that should be running right now)
 */
export async function playCurrentDisplay(): Promise<void> {
    try {
        return await getCurrentDisplay().playAllZones();
    } catch (supererror) {
        // need to swallow the error because the layout code needs to keep going
        // regardless of whether all of the zones started properly or not
        Log.Warning(`Error playing Current Display: ${supererror}`, null, __fileline);
    }
}

/**
 * Stops all the zones in the currently running display
 */
export async function stopCurrentDisplay(): Promise<void> {
    try {
        return await getCurrentDisplay().stopAllZones();
    } catch (greaterror) {
        // need to swallow the error because the layout code needs to keep going
        // regardless of whether all of the zones stopped properly or not
        Log.Warning(`Error stopping Current Display: ${greaterror}`, null, __fileline);
    }
}

/**
 * Get Display's configuration via ID
 * @throws {ReferenceError} if not found
 */
export function getDisplayById(id: number): Display {
    const filtered = displays.filter((display) => display.id === id);
    if (filtered.length < 1) {
        throw new ReferenceError(`Could not find Display with ID #${id}`);
    }
    return filtered[0];
}

/**
 * Get a Zone's parent Display configuration via the Zone's ID
 * @throws {ReferenceError} if not found
 */
export function getDisplayByZoneId(zoneId: number): Display {
    const zoneData = displays
        .map((display) =>
            display.zones.map((z) => {
                return { zone: z, display: display };
            })
        )
        .flat();
    const filtered = zoneData.filter((zd) => zd.zone.id === zoneId);
    if (filtered.length < 1) {
        throw new ReferenceError(`Could not find Display containing Zone with ID #${zoneId}`);
    }
    return filtered[0].display;
}

/**
 * Get Zone's configuration via ID
 * @throws {ReferenceError} if not found
 */
export function getZoneById(id: number): Zone {
    const zones = displays.map((display) => display.zones).flat();
    const filtered = zones.filter((zone) => zone.id === id);
    if (filtered.length < 1) {
        throw new ReferenceError(`Could not find Zone with ID #${id}`);
    }
    return filtered[0];
}
